import { OverlaySpinner } from "@components/Loading/OverlaySpinner";
import { FirebaseContext } from "@helpers/firebase";
import { useSimpleCollectionOnce } from "@helpers/useSimpleCollectionOnce";
import { lazy, Suspense, useContext, useEffect, useMemo, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";

// Lazy load admin-only components
const ChartJSDashboard = lazy(() =>
  import("./ChartJSDashboard").then((module) => ({ default: module.ChartJSDashboard })),
);
const DiscountManager = lazy(() => import("./DiscountManager").then((module) => ({ default: module.DiscountManager })));
const EventTable = lazy(() =>
  import("@components/Table/EventTable").then((module) => ({ default: module.EventTable })),
);

const StatsWidget = ({ title, value, color }: { title: string; value: number | string; color: string }) => (
  <div className={`stats-stat bg-gradient-to-br opacity-80 ${color} px-8 py-4 text-white`}>
    <div className="stat-title text-gray-100">{title}</div>
    <div className="stat-value text-white">{typeof value === "number" ? value.toLocaleString() : value}</div>
  </div>
);

export const Report = () => {
  const { functions } = useContext(FirebaseContext);

  // Events table (tab 1): read straight from Firestore so it renders immediately,
  // without waiting on the (cold-starting, metrics-heavy) getAllEvents function.
  const [allEvents, eventsLoading, eventsError] = useSimpleCollectionOnce<AppEvent & { id: string }>("events");
  const events = useMemo<BasicEvent[]>(
    () =>
      allEvents.map((event) => ({
        id: event.id,
        name: event.name,
        date: event.date,
        isPremium: event.isPremium ?? false,
        isPromo: event.isPromo ?? false,
        type: event.type,
        email: event.email,
      })),
    [allEvents],
  );

  // Business metrics (tab 2): heavier server-side aggregation, fetched in the background.
  const [getAllEvents, loadingMetrics, metricsError] = useHttpsCallable<
    API.GetAllEventsRequest,
    API.GetAllEventsResponse
  >(functions, "getAllEvents");
  const [metrics, setMetrics] = useState<API.MetricData[]>([]);
  const [conversionAnalytics, setConversionAnalytics] = useState<API.ConversionAnalytics[]>([]);
  const [avgDaysToConversion, setAvgDaysToConversion] = useState<number>(0);
  const [globalConversionRate, setGlobalConversionRate] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<number>(1);
  const [brandFilter, setBrandFilter] = useState<"all" | "camdeed" | "tuokis">("all");

  // Calculate brand-specific and total statistics
  const getBrandMetrics = (brand: "all" | "camdeed" | "tuokis") => {
    if (brand === "all") {
      return {
        events: metrics.reduce((sum, metric) => sum + metric.events, 0),
        premiumEvents: metrics.reduce((sum, metric) => sum + metric.premiumEvents, 0),
        revenue: Math.round(metrics.reduce((sum, metric) => sum + metric.revenue, 0) * 100) / 100,
      };
    } else if (brand === "camdeed") {
      return {
        events: metrics.reduce((sum, metric) => sum + (metric.camdeedEvents || 0), 0),
        premiumEvents: metrics.reduce((sum, metric) => sum + (metric.camdeedPremiumEvents || 0), 0),
        revenue: Math.round(metrics.reduce((sum, metric) => sum + (metric.camdeedRevenue || 0), 0) * 100) / 100,
      };
    } else {
      return {
        events: metrics.reduce((sum, metric) => sum + (metric.tuokisEvents || 0), 0),
        premiumEvents: metrics.reduce((sum, metric) => sum + (metric.tuokisPremiumEvents || 0), 0),
        revenue: Math.round(metrics.reduce((sum, metric) => sum + (metric.tuokisRevenue || 0), 0) * 100) / 100,
      };
    }
  };

  const currentMetrics = getBrandMetrics(brandFilter);
  const totalEvents = currentMetrics.events;
  const totalPremiumEvents = currentMetrics.premiumEvents;
  const totalRevenue = currentMetrics.revenue;
  const conversionRate = totalEvents > 0 ? Math.round((totalPremiumEvents / totalEvents) * 100) : 0;

  // Get brand metrics for comparison
  const camdeedMetrics = getBrandMetrics("camdeed");
  const tuokisMetrics = getBrandMetrics("tuokis");

  useEffect(() => {
    if (eventsError) console.error("Error loading events:", eventsError);
    if (metricsError) console.error("Error loading metrics:", metricsError);
  }, [eventsError, metricsError]);

  // Kicked off on mount but never blocks the events table — only tab 2 depends on it.
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const result = await getAllEvents();
        if (result?.data) {
          setMetrics(result.data.metrics);
          setConversionAnalytics(result.data.conversionAnalytics || []);
          setAvgDaysToConversion(result.data.avgDaysToConversion || 0);
          setGlobalConversionRate(result.data.conversionRate || 0);
        }
      } catch (error) {
        console.error("Error fetching metrics:", error);
      }
    };

    fetchMetrics();
  }, [getAllEvents]);

  return (
    <div className="flex flex-col">
      <main className="flex-1 flex-col">
        <div className="flex flex-col py-4">
          <h1 className="mt-12 text-center text-3xl font-semibold">Admin Reports</h1>
          <div className="mt-4 flex w-full flex-col items-center px-4">
            <div className="h-100 flex w-full max-w-6xl flex-col items-center py-4">
              <div className="tabs mb-8 mt-8 w-full max-w-lg">
                <a
                  className={`tab-bordered tab ${
                    activeTab === 1 ? "tab-active" : ""
                  } w-1/3 whitespace-nowrap text-xs sm:text-sm`}
                  onClick={() => setActiveTab(1)}
                >
                  Upcoming Events
                </a>
                <a
                  className={`tab-bordered tab ${
                    activeTab === 2 ? "tab-active" : ""
                  } w-1/3 whitespace-nowrap text-xs sm:text-sm`}
                  onClick={() => setActiveTab(2)}
                >
                  Business Metrics
                </a>
                <a
                  className={`tab-bordered tab ${
                    activeTab === 3 ? "tab-active" : ""
                  } w-1/3 whitespace-nowrap text-xs sm:text-sm`}
                  onClick={() => setActiveTab(3)}
                >
                  Discount Manager
                </a>
              </div>

              <div className={`w-full ${!activeTab || activeTab === 1 ? "" : "hidden"}`}>
                <OverlaySpinner loading={eventsLoading} />
                <EventTable data={events} />
              </div>
              <div className={`w-full ${activeTab === 2 ? "" : "hidden"} relative`}>
                <OverlaySpinner loading={loadingMetrics} />
                <div className="fixed bottom-6 right-6 z-50">
                  <div className="flex items-center space-x-4 rounded-xl border border-gray-200 bg-white px-6 py-4 shadow-lg backdrop-blur-sm">
                    <span className="text-base font-semibold text-gray-700">Brand:</span>
                    <select
                      value={brandFilter}
                      onChange={(e) => setBrandFilter(e.target.value as "all" | "camdeed" | "tuokis")}
                      className="min-w-[140px] rounded-lg border border-gray-300 bg-white px-4 py-3 text-base font-medium transition-all duration-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                    >
                      <option value="all">All Brands</option>
                      <option value="camdeed">Camdeed</option>
                      <option value="tuokis">Tuokis</option>
                    </select>
                  </div>
                </div>

                <div className="mb-8 mt-12">
                  <h2 className="mb-6 text-center text-xl font-semibold">
                    {brandFilter === "all" ? "All Brands" : brandFilter === "camdeed" ? "Camdeed" : "Tuokis"} - Totals
                  </h2>
                  <div className="stats stats-vertical w-full shadow lg:stats-horizontal">
                    <StatsWidget title="Total Events" value={totalEvents} color="from-blue-700 to-blue-800" />
                    <StatsWidget
                      title="Premium Events"
                      value={totalPremiumEvents}
                      color="from-amber-700 to-amber-800"
                    />
                    <StatsWidget title="Total Revenue" value={`€${totalRevenue}`} color="from-green-700 to-green-800" />
                    <StatsWidget
                      title="Conversion Rate"
                      value={`${Math.round(conversionRate)}%`}
                      color="from-purple-700 to-purple-800"
                    />
                  </div>
                </div>

                {/* Brand Comparison Section */}
                {brandFilter === "all" && (
                  <div className="mb-8 mt-8">
                    <h2 className="mb-6 text-center text-xl font-semibold">Brand Comparison</h2>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {/* Camdeed Stats */}
                      <div className="rounded-lg bg-white p-6 shadow-sm">
                        <h3 className="mb-4 text-center text-lg font-semibold text-blue-600">Camdeed</h3>
                        <div className="stats stats-vertical w-full">
                          <StatsWidget title="Events" value={camdeedMetrics.events} color="from-blue-600 to-blue-700" />
                          <StatsWidget
                            title="Premium Events"
                            value={camdeedMetrics.premiumEvents}
                            color="from-blue-600 to-blue-700"
                          />
                          <StatsWidget
                            title="Revenue"
                            value={`€${camdeedMetrics.revenue}`}
                            color="from-blue-600 to-blue-700"
                          />
                          <StatsWidget
                            title="Conversion Rate"
                            value={`${Math.round(
                              camdeedMetrics.events > 0
                                ? (camdeedMetrics.premiumEvents / camdeedMetrics.events) * 100
                                : 0,
                            )}%`}
                            color="from-blue-600 to-blue-700"
                          />
                        </div>
                      </div>

                      {/* Tuokis Stats */}
                      <div className="rounded-lg bg-white p-6 shadow-sm">
                        <h3 className="mb-4 text-center text-lg font-semibold text-green-600">Tuokis</h3>
                        <div className="stats stats-vertical w-full">
                          <StatsWidget
                            title="Events"
                            value={tuokisMetrics.events}
                            color="from-green-600 to-green-700"
                          />
                          <StatsWidget
                            title="Premium Events"
                            value={tuokisMetrics.premiumEvents}
                            color="from-green-600 to-green-700"
                          />
                          <StatsWidget
                            title="Revenue"
                            value={`€${tuokisMetrics.revenue}`}
                            color="from-green-600 to-green-700"
                          />
                          <StatsWidget
                            title="Conversion Rate"
                            value={`${Math.round(
                              tuokisMetrics.events > 0 ? (tuokisMetrics.premiumEvents / tuokisMetrics.events) * 100 : 0,
                            )}%`}
                            color="from-green-600 to-green-700"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-8 mt-8">
                  <h2 className="mb-6 text-center text-xl font-semibold">Conversion Analytics</h2>
                  <div className="stats stats-vertical w-full shadow lg:stats-horizontal">
                    <StatsWidget
                      title="Avg Days to Conversion"
                      value={`${avgDaysToConversion} days`}
                      color="from-indigo-700 to-indigo-800"
                    />
                    <StatsWidget
                      title="Global Conversion Rate"
                      value={`${globalConversionRate}%`}
                      color="from-pink-700 to-pink-800"
                    />
                    <StatsWidget
                      title="Events with Analytics"
                      value={conversionAnalytics.length}
                      color="from-teal-700 to-teal-800"
                    />
                    <StatsWidget
                      title="Converted Events"
                      value={conversionAnalytics.filter((e) => e.hasConverted).length}
                      color="from-orange-700 to-orange-800"
                    />
                  </div>
                </div>

                <h2 className="mt-20 mb-6 text-center text-xl font-semibold">Dashboards</h2>

                <Suspense fallback={<OverlaySpinner loading={true} />}>
                  <ChartJSDashboard
                    data={metrics}
                    conversionAnalytics={conversionAnalytics}
                    avgDaysToConversion={avgDaysToConversion}
                    globalConversionRate={globalConversionRate}
                    brandFilter={brandFilter}
                  />
                </Suspense>
              </div>

              <div className={`w-full ${activeTab === 3 ? "" : "hidden"}`}>
                <Suspense fallback={<OverlaySpinner loading={true} />}>
                  <DiscountManager />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
